<template>
  <div class="wrapper" :class="bgImageUrl ? 'background_shadow' : ''" :style="{ backgroundImage: bgImageUrl ? `url(${bgImageUrl})` : '' }">
    <div class="block-wrapper">
      <div class="presentation">
        <div class="title">{{ $t("game.homeView.welcomeTitle", { firstName: talentName }) }}</div>
        <div class="block">
          <h2>{{ $t("game.homeView.welcomeHeader") }}</h2>
          <p>{{ $t("game.homeView.welcomeDescription") }}</p>
        </div>
      </div>
      <div class="no-campaign" v-if="assessmentStore.assessments.length === 0">
        {{ $t('game.homeView.noCampaign') }}
      </div>
      <div v-else-if="assessmentStore.assessments.length == 1" class="assessments-wrapper">
        <AssessmentCard v-for="(assessment, i) in assessmentStore.assessments" :key="assessment.campaign_id" :assessment="assessment" :isMobile="state.windowWidth < 1024" :isMobileEnabled="state.isMobileEnabled" @startAssessment="startAssessment(assessment)" />
      </div>
      <carousel v-else class="slider" snap-align="center" :wrap-around="true" :transition="600" :breakpoints="state.carouselBreakpoints" ref="gameCarousel" @slide-end="handleSlideChanged">
        <Slide v-for="(assessment, index) in assessmentStore.assessments" :key="index" @click="gameCarousel.slideTo(index); changeLocaleWithCampaignLanguage(index)" class="slide">
          <AssessmentCard :assessment="assessment" :isMobile="state.windowWidth < 1024" :isMobileEnabled="state.isMobileEnabled" @startAssessment="startAssessment(assessment)" />
        </Slide>
        <template #addons>
          <Pagination />
        </template>
      </carousel>
    </div>
    <div class="actions-btn">
      <Button class="icon icon-left secondary sm no-dark-mode" :label="$t('game.homeView.accountSettingsButton')" icon="bi-gear" @click="state.showAccountModal = true" />
    </div>
    <!-- <LanguagePicker class="grouped-button" /> -->
    <AccountModal :language="locale" :isOpen="state.showAccountModal" @close="state.showAccountModal = false" />
    <LaunchConfirmation :isOpen="state.showLaunchConfirmation" @close="state.showLaunchConfirmation = false" />
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/variables.scss";

.block-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;

  .no-campaign {
    text-align: center;
    margin-top: 32px;
    font-size: 14px;
    color: $primary-color;
  }

  .presentation {
    display: flex;
    flex-direction: column;
    text-align: center;
    justify-content: center;

    .title {
      margin: auto !important;
      margin-top: 16px !important;
      margin-bottom: 8px !important;
    }
    .block {
      margin: auto;
    }
  }

  @media screen and (min-width: $small-screen) and (max-width: $medium-screen) {
    flex-direction: row;
    justify-content: space-evenly;
  }
}

.grouped-button {
  position: absolute;
  top: 96px;
  right: 48px;

  display: flex;
  gap: 4px;
  padding: 4px;
  border: 1px solid $secondary-color;
  border-radius: 4px;
  flex: 0;

  @media screen and (max-width: $small-screen) {
    max-width: 60%;
    top: 16px;
    left: 16px;
  }

  @media screen and (max-width: $large-screen) and (min-width: $medium-screen) {
    .btn {
      font-size: 12px;
      padding: 6px 14px;
    }
  }

  .btn.secondary {
    background-color: $secondary-800-color;
    color: $white-color;

    &:hover {
      background-color: $secondary-800-color;
    }

    &:active {
      background-color: $secondary-600-color;
    }
  }
}
</style>

<script setup lang="ts">
import { ref, reactive, onMounted } from "vue";
import { Carousel, Slide, Pagination } from "vue3-carousel";
import { useI18n } from "vue-i18n";
import { useCandidateStore } from "@/stores/candidate";
import { useAssessmentStore } from "@/stores/assessment";
import { useCloudClientStore } from "@/stores/cloud-client.js";
import AssessmentCard from "./partials/AssessmentCard.vue";
import AccountModal from "@/components/modals/game/account/AccountModal.vue";
import LaunchConfirmation from "@/components/modals/game/LaunchConfirmation.vue";
import "vue3-carousel/dist/carousel.css";
import { getImage } from "@/composables/getImage.js";
import { Participant } from "@/types/participant";

const gameCarousel = ref(null);
const { t, locale } = useI18n();
const { campaignMajorVersion } = useSkillsStore();

const candidateStore = useCandidateStore();
const router = useRouter();
const route = useRoute();

const assessmentStore = useAssessmentStore();
const cloudClientStore = useCloudClientStore();

if (assessmentStore.assessments.length < 1) {
  await assessmentStore.getCandidateAssessments();
}

const state = reactive({
  windowWidth: 0,
  showAccountModal: false,
  showLaunchConfirmation: false,
  showVerifyPhone: false,
  carouselBreakpoints: {
    0: {
      itemsToShow: 1,
    },
    767: {
      itemsToShow: 2,
    },
    1000: {
      itemsToShow: 3,
    },
  },
  isMobileEnabled: false
});

const talentName = computed(() => {
  return candidateStore?.candidate?.personal_information?.first_name || "";
})

onMounted(() => {
  state.windowWidth = window.innerWidth;

  window.addEventListener("resize", () => {
    if (gameCarousel.value) {
      gameCarousel.value.updateSlideWidth();
    }

    state.windowWidth = window.innerWidth;
  });

  const currentCampaign = assessmentStore?.assessments?.[0]?.campaign;
  state.isMobileEnabled = import.meta.env.VITE_ALLOW_MOBILE === "true";

  locale.value = currentCampaign?.campaign?.locale || 'fr';
});

function changeLocaleWithCampaignLanguage(index: number) {
  const currentCampaign =  assessmentStore.assessments[index].campaign;
  locale.value = currentCampaign?.campaign?.locale || 'fr';
}

function startAssessment(assessment: Participant) {
  cloudClientStore.init(assessment.participant_id, campaignMajorVersion(assessment.campaign), assessment.campaign.created_at, assessment.campaign.calibration_id);
  router.replace({
    query: {
      ...route.query,
      assessmentId: assessment?.campaign?.id,
    }
  })
  state.showLaunchConfirmation = true;
}

const bgImageUrl = ref(null);

function handleSlideChanged(slideData) {
  setBGImageFromA(assessmentStore.assessments[slideData.currentSlideIndex]);
}

function setBGImageFromA(assessment: Participant) {
  if (assessment?.campaign?.branding?.background_image_id) {
    const imageBGUrl = getImage(assessment?.campaign?.branding?.background_image_id);
    bgImageUrl.value = imageBGUrl;
  } else {
    bgImageUrl.value = false;
  }
}

setBGImageFromA(assessmentStore.assessments[0]);
</script>
